/**
 * Safe replacement for object-path library
 * Uses lodash for better security (no prototype pollution)
 */
import _get from 'lodash/get';
import _has from 'lodash/has';
import _set from 'lodash/set';

const objectPathHelper = {
  /**
   * Get a value from an object using a path
   * @param {Object} obj - The object to query
   * @param {String|Array} path - The path of the property to get
   * @param {*} defaultValue - The value returned if the path doesn't exist
   * @returns {*} The value at path or defaultValue
   */
  get: (obj, path, defaultValue) => {
    return _get(obj, path, defaultValue);
  },

  /**
   * Check if a path exists in an object
   * @param {Object} obj - The object to query
   * @param {String|Array} path - The path to check
   * @returns {Boolean} Returns true if path exists, else false
   */
  has: (obj, path) => {
    return _has(obj, path);
  },

  /**
   * Push a value to an array at the given path
   * If the path doesn't exist, it will be created
   * @param {Object} obj - The object to modify
   * @param {String|Array} path - The path of the array
   * @param {...*} values - The values to push
   * @returns {Object} Returns the object
   */
  push: (obj, path, ...values) => {
    const currentValue = _get(obj, path);
    if (Array.isArray(currentValue)) {
      currentValue.push(...values);
    } else {
      _set(obj, path, values);
    }
    return obj;
  },

  /**
   * Set a value at the given path
   * @param {Object} obj - The object to modify
   * @param {String|Array} path - The path of the property to set
   * @param {*} value - The value to set
   * @returns {Object} Returns the object
   */
  set: (obj, path, value) => {
    return _set(obj, path, value);
  }
};

export default objectPathHelper;
